import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  dismissAnnouncement,
  isAnnouncementDismissed,
} from "helpers/contentful/normalizeSiteAnnouncement";
import { isMeetPagePath, MEET_PAGE_ENABLED } from "config/featureFlags";
import "./SiteAnnouncementBanner.css";

const isInternalLink = (url) => url.startsWith("/") && !url.startsWith("//");

const SiteAnnouncementBanner = () => {
  const location = useLocation();
  const announcement = useSelector(
    (state) => state.contentfulReducer.siteAnnouncement
  );
  const [visible, setVisible] = useState(false);
  const hideOnMeetPage = isMeetPagePath(location.pathname);

  useEffect(() => {
    if (!announcement || hideOnMeetPage) {
      setVisible(false);
      return;
    }

    if (announcement.dismissible && isAnnouncementDismissed(announcement.id)) {
      setVisible(false);
      return;
    }

    setVisible(true);
  }, [announcement, hideOnMeetPage]);

  const handleDismiss = useCallback(() => {
    if (!announcement) return;
    dismissAnnouncement(announcement.id);
    setVisible(false);
  }, [announcement]);

  if (!announcement || !visible || hideOnMeetPage) return null;

  const { message, linkUrl, linkText, variant, dismissible } = announcement;
  const showLink =
    linkUrl && (MEET_PAGE_ENABLED || !isMeetPagePath(linkUrl));

  return (
    <div
      className={`site-announcement site-announcement--${variant}`}
      role="region"
      aria-label="Announcement"
    >
      <div className="site-announcement__inner">
        <p className="site-announcement__message">
          <span>{message}</span>
          {showLink &&
            (isInternalLink(linkUrl) ? (
              <Link
                to={linkUrl}
                className="site-announcement__link"
                data-track="site_announcement_click"
                data-track-location="banner"
                data-track-label={variant}
                data-track-item={announcement.id}
              >
                {linkText}
              </Link>
            ) : (
              <a
                href={linkUrl}
                className="site-announcement__link"
                target="_blank"
                rel="noopener noreferrer"
                data-track="site_announcement_click"
                data-track-location="banner"
                data-track-label={variant}
                data-track-item={announcement.id}
              >
                {linkText}
              </a>
            ))}
        </p>

        {dismissible && (
          <button
            type="button"
            className="site-announcement__dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss announcement"
            data-track="site_announcement_dismiss"
            data-track-location="banner"
            data-track-label={announcement.id}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default SiteAnnouncementBanner;
